import { atom } from 'jotai'
import {
  AggregatedInvoice,
  AggregatedLineItemGroupMutation
} from 'Invoices/types'
import { formatDateRange } from '@sequencehq/utils/dates'
import { getHumanReadableInvoiceNumber } from 'Invoices/utils'
import { toCurrencySymbol } from '@sequencehq/utils'
import {
  currentCreditNotes,
  currentPayments
} from 'Invoices/utils/currentlyApplied'

// Do not update this atom directly. Use clientInvoiceAtom instead.
const _internalClientInvoiceAtom = atom<AggregatedInvoice | null>(null)

export const clientInvoiceUpdatedTimeAtom = atom<number | null>(null)

export const clientInvoiceSaveStatusAtom = atom(false)

// This is the only atom that should be used to update the client invoice
// The setter partially updates the invoice.
// It ensures timestamps are kept in sync for persistence logic.
export const clientInvoiceAtom = atom(
  get => get(_internalClientInvoiceAtom),
  (get, set, updates: Partial<AggregatedInvoice>) => {
    const clientInvoice = get(_internalClientInvoiceAtom)

    set(_internalClientInvoiceAtom, {
      ...(clientInvoice ?? ({} as AggregatedInvoice)),
      ...updates
    })

    set(clientInvoiceUpdatedTimeAtom, Date.now())
  }
)

// Replaces the client copy with the server's without recording a client edit.
// clientInvoiceAtom would stamp the updated time, making the autosave treat
// hydration as an edit and save on load.
export const resetClientInvoiceFromServerAtom = atom(
  null,
  (_get, set, serverInvoice: AggregatedInvoice) => {
    set(_internalClientInvoiceAtom, serverInvoice)
    set(clientInvoiceUpdatedTimeAtom, null)
  }
)

export const invoiceIdAtom = atom(get => get(clientInvoiceAtom)?.id)

export const invoiceStatusAtom = atom(get => get(clientInvoiceAtom)?.status)

export const invoiceIsEditableAtom = atom(
  get => get(invoiceStatusAtom) === 'DRAFT'
)

export const invoiceCurrencyAtom = atom(get => get(clientInvoiceAtom)?.currency)
export const invoiceCurrencySymbolAtom = atom(get =>
  toCurrencySymbol(get(invoiceCurrencyAtom) ?? 'USD')
)

export const invoiceGrossTotalAtom = atom(
  get => get(clientInvoiceAtom)?.grossTotal
)

export const invoiceHasNegativeTotalAtom = atom(get => {
  const grossTotal = get(invoiceGrossTotalAtom)
  return grossTotal ? parseFloat(grossTotal) < 0 : false
})

export const invoiceNetTotalAtom = atom(get => get(clientInvoiceAtom)?.netTotal)

export const invoiceAppliedCreditNotesAtom = atom(get => {
  const invoice = get(clientInvoiceAtom)
  return invoice ? currentCreditNotes(invoice) : []
})

export const invoiceAppliedCreditGrantsAtom = atom(
  get => get(clientInvoiceAtom)?.appliedCreditGrants ?? []
)

export const invoiceAppliedCreditGrantTaxAtom = atom(
  get => get(clientInvoiceAtom)?.appliedCreditGrantTax
)

export const invoiceProjectedCreditGrantsAtom = atom(
  get => get(clientInvoiceAtom)?.projectedCreditGrants ?? []
)

export const invoicePaymentsAtom = atom(get => {
  const invoice = get(clientInvoiceAtom)
  return invoice ? currentPayments(invoice) : []
})

export const invoiceBalanceAtom = atom(get => get(clientInvoiceAtom)?.balance)

export const invoiceAccountingDateAtom = atom(
  get => get(clientInvoiceAtom)?.accountingDate
)

export const invoiceBillingPeriodAtom = atom(
  get => get(clientInvoiceAtom)?.billingPeriod
)

export const invoiceBillingPeriodDatesAtom = atom(get => {
  const billingPeriod = get(invoiceBillingPeriodAtom)

  if (!billingPeriod) return undefined

  return {
    start: new Date(billingPeriod.start),
    endInclusive: new Date(billingPeriod.endInclusive)
  }
})

export const invoiceMemoAtom = atom(get => get(clientInvoiceAtom)?.memo)
export const invoicePurchaseOrderNumberAtom = atom(
  get => get(clientInvoiceAtom)?.purchaseOrderNumber
)
export const invoiceReferenceAtom = atom(
  get => get(clientInvoiceAtom)?.reference
)

export const humanReadableInvoiceNumberAtom = atom(get => {
  const invoiceNumber = get(clientInvoiceAtom)?.invoiceNumber
  const invoiceStatus = get(invoiceStatusAtom)

  return getHumanReadableInvoiceNumber({ invoiceNumber, invoiceStatus })
})

export const humanReadableDatesAtom = atom(get => {
  const billingPeriodDates = get(invoiceBillingPeriodDatesAtom)

  return {
    billingPeriod: billingPeriodDates
      ? formatDateRange({
          from: billingPeriodDates.start,
          to: billingPeriodDates.endInclusive
        })
      : undefined
  }
})

export const customerIntegrationIdsAtom = atom(
  get => get(clientInvoiceAtom)?.customer.integrationIds
)

export const invoiceCustomerIdAtom = atom(
  get => get(clientInvoiceAtom)?.customerId
)

export const invoiceCustomerTaxStatusAtom = atom(
  get => get(clientInvoiceAtom)?.customerTaxStatus
)

export const invoiceCustomerTaxIdAtom = atom(
  get => get(clientInvoiceAtom)?.customerTaxId
)

export const invoiceCustomerBillingAddressAtom = atom(
  get => get(clientInvoiceAtom)?.customerBillingAddress
)

export const invoiceCustomerLegalCompanyNameAtom = atom(
  get => get(clientInvoiceAtom)?.customerLegalCompanyName
)

export const invoicePaymentStatusAtom = atom(
  get => get(clientInvoiceAtom)?.paymentStatus
)

export const invoiceGoCardlessIntegrationPaymentStatusAtom = atom(
  get => get(clientInvoiceAtom)?.integrationPaymentStatuses?.goCardless
)

export const billingScheduleIdAtom = atom(
  get => get(clientInvoiceAtom)?.billingScheduleId
)

export const invoiceCalculatedAtAtom = atom(
  get => get(clientInvoiceAtom)?.calculatedAt
)

export const invoiceSubAccountUsageAtom = atom(
  get => get(clientInvoiceAtom)?.subAccountUsage
)

export const invoiceAttachmentsAtom = atom(
  get => get(clientInvoiceAtom)?.attachmentAssets ?? []
)

export const paymentOptionsAtom = atom(
  get => get(clientInvoiceAtom)?.paymentOptions ?? []
)

export const paymentLinkProviderAtom = atom(
  get => get(clientInvoiceAtom)?.renderSettings?.paymentLinkProvider
)

export const lineItemGroupsAtom = atom(
  get => get(clientInvoiceAtom)?.lineItemGroups,
  (get, set, updatedLineItemGroups: AggregatedLineItemGroupMutation[]) => {
    const clientInvoice = get(clientInvoiceAtom)

    if (!clientInvoice) return

    const existingLineItemGroups = clientInvoice.lineItemGroups
    const invoiceId = clientInvoice.id

    set(clientInvoiceAtom, {
      lineItemGroups: updatedLineItemGroups.map(
        (lineItemGroup, lineItemGroupIndex) => {
          const existingLineItemGroup = existingLineItemGroups.find(
            li => li.id === lineItemGroup.id
          )

          return {
            ...lineItemGroup,
            invoiceId,
            /**
             * The BE starts line item group indexes at 1, so match that here
             */
            index: lineItemGroupIndex + 1,
            netTotal: existingLineItemGroup?.netTotal ?? '0',
            totalTax: existingLineItemGroup?.totalTax ?? '0',
            grossTotal: existingLineItemGroup?.grossTotal ?? '0',
            lineItems: lineItemGroup.lineItems.map(
              (lineItem, lineItemIndex) => {
                const existingLineItem = existingLineItemGroup?.lineItems.find(
                  li => li.id === lineItem.id
                )

                return {
                  ...lineItem,
                  invoiceId,
                  index: lineItemIndex,
                  netTotal: existingLineItem?.netTotal ?? '0',
                  totalTax: existingLineItem?.totalTax ?? '0',
                  grossTotal: existingLineItem?.grossTotal ?? '0',
                  taxRate: existingLineItem?.taxRate ?? '0',
                  groupId: lineItemGroup.id
                }
              }
            )
          }
        }
      )
    })
  }
)

export const lineItemGroupsCountAtom = atom(
  get => get(lineItemGroupsAtom)?.length ?? 0
)

export const deleteLineItemAtom = atom(
  null,
  (get, set, lineItem: { groupId: string; id: string }) => {
    const clientInvoice = get(clientInvoiceAtom)

    if (!clientInvoice) return null

    set(clientInvoiceAtom, {
      lineItemGroups: clientInvoice.lineItemGroups.map(lineItemGroup => {
        if (lineItemGroup.id !== lineItem.groupId) {
          return lineItemGroup
        }

        return {
          ...lineItemGroup,
          lineItems: lineItemGroup.lineItems.filter(li => li.id !== lineItem.id)
        }
      })
    })
  }
)

export const deleteLineItemGroupAtom = atom(null, (get, set, id: string) => {
  const clientInvoice = get(clientInvoiceAtom)

  if (!clientInvoice) return null

  set(clientInvoiceAtom, {
    lineItemGroups: clientInvoice.lineItemGroups.filter(
      group => group.id !== id
    )
  })
})

// When this is explicitly set to true, the invoice will be concerned with bulk actions
// This is a design decision deliberately made to avoid polling for bulk actions more than necessary
// However, it does mean if the user navigates away from the invoice page, bulk actions in flight will not be polled for when they return
// Those bulk actions will still be polled for on the invoices list page.
export const awaitingBulkActionAtom = atom(false)

export const unsyncedIdsAtom = atom({
  lineItemGroupIds: new Set<string>(),
  lineItemIds: new Set<string>()
})

export const isSavePendingAtom = atom(get => {
  // A pending bulk action is roughly equivalent to a pending save
  if (get(awaitingBulkActionAtom)) {
    return true
  }

  const unsyncedIds = get(unsyncedIdsAtom)
  return (
    unsyncedIds.lineItemGroupIds.size > 0 || unsyncedIds.lineItemIds.size > 0
  )
})
